import { useState, useEffect } from 'react';
import { X, Buildings } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase';

export default function RequestAssignmentModal({ isOpen, onClose, userId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedSubgroup, setSelectedSubgroup] = useState('');

  // Fetch teams (depth 1) on mount
  useEffect(() => {
    if (!isOpen) return;

    const fetchTeams = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single();

      const { data, error } = await supabase
        .from('groups')
        .select('id, name')
        .eq('org_id', userData.org_id)
        .eq('depth', 1)
        .order('name');

      if (error) {
        console.error('Error fetching teams:', error);
      } else {
        setTeams(data || []);
      }
      setLoading(false);
    };

    fetchTeams();
  }, [isOpen]);

  // Fetch groups when team selected
  useEffect(() => {
    if (!selectedTeam) {
      setGroups([]);
      setSelectedGroup('');
      setSubgroups([]);
      setSelectedSubgroup('');
      return;
    }

    const fetchGroups = async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('id, name')
        .eq('parent_id', selectedTeam)
        .eq('depth', 2)
        .order('name');

      if (error) {
        console.error('Error fetching groups:', error);
      } else {
        setGroups(data || []);
      }
    };

    fetchGroups();
  }, [selectedTeam]);

  // Fetch subgroups when group selected
  useEffect(() => {
    if (!selectedGroup) {
      setSubgroups([]);
      setSelectedSubgroup('');
      return;
    }

    const fetchSubgroups = async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('id, name')
        .eq('parent_id', selectedGroup)
        .eq('depth', 3)
        .order('name');

      if (error) {
        console.error('Error fetching subgroups:', error);
      } else {
        setSubgroups(data || []);
      }
    };

    fetchSubgroups();
  }, [selectedGroup]);

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!selectedSubgroup) {
    alert('Please select a subgroup');
    return;
  }

  setSubmitting(true);

  try {
    // Get user's organization_id
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data: userData } = await supabase
      .from('users')
      .select('org_id')
      .eq('id', user.id)
      .single();

    if (!userData?.org_id) {
      throw new Error('User organization not found');
    }

    // Insert assignment request WITH organization_id
    const { error } = await supabase
      .from('assignment_requests')
      .insert({
        user_id: userId,
        organization_id: userData.org_id, // ← This was missing!
        requested_subgroup_id: selectedSubgroup,
        status: 'pending'
      });

    if (error) throw error;

    alert('Request submitted successfully!');
    onSuccess?.();
    onClose();
  } catch (error) {
    console.error('Error submitting request:', error);
    alert('Failed to submit request. Please try again.');
  } finally {
    setSubmitting(false);
  }
};

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-xl">
              <Buildings size={24} weight="light" className="text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Request Assignment</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X size={24} weight="light" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading groups...</div>
          ) : (
            <>
              {/* Team Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Team
                </label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  disabled={teams.length === 0}
                >
                  <option value="">Select a team</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Group Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Group
                </label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                  disabled={!selectedTeam || groups.length === 0}
                >
                  <option value="">Select a group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subgroup Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Subgroup <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedSubgroup}
                  onChange={(e) => setSelectedSubgroup(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                  disabled={!selectedGroup || subgroups.length === 0}
                  required
                >
                  <option value="">Select a subgroup</option>
                  {subgroups.map((subgroup) => (
                    <option key={subgroup.id} value={subgroup.id}>
                      {subgroup.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  Your request will be sent to the administrators for approval. You'll be notified once your request is reviewed.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!selectedSubgroup || submitting}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors border border-blue-700"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}